import { Button, Skeleton, Textarea } from '@driftt/dds'
import { faSparkles } from '@driftt/dds.icons/regular/faSparkles'
import React, { useEffect } from 'react'
import { useFormContext } from 'react-hook-form'

import {
  AILQTrackAction,
  useLQTracking,
} from 'components/AIConfiguration/LeadQualification/useLQTracking'
import { useGPTQualificationAttributes } from 'hooks/useGPTQualificationAttributes'

import { Attribute } from '../../types'
import { transformAttributes } from '../helpers'
import { PersonaForm } from '../types'

interface Props {
  disabled: boolean
  isLoading?: boolean
  onGeneratingAttributes: (config: { isLoading: boolean }) => void
}

const PROMPT_PLACEHOLDER =
  'A qualified lead has at least 1000 or more employees. ' +
  'Their budget ranges from 100k to 500k. ' +
  'The lead would be located in the US, Canada, or Ireland. ' +
  'Their industry should be within Biotechnology or Pharmaceuticals.'

const PROMPT_DESCRIPTION =
  'Enter the attributes you want from a qualified lead. ' +
  'To simplify the process, we recommend you split each attribute into separate sentences, e.g., the company size is at least 100 employees. ' +
  'They use Salesforce, Pardot, Marketo, or Hubspot.'

const AIPrompt = ({
  disabled,
  isLoading = false,
  onGeneratingAttributes,
}: Props) => {
  const {
    register,
    unregister,
    getValues,
    getFieldState,
    resetField,
    setValue,
    watch,
  } = useFormContext<PersonaForm>()
  const prompt = watch('prompt')

  const { isDirty } = getFieldState('prompt')
  const isDiscoverAttributesDisabled =
    !isDirty || disabled || isLoading || !prompt

  const { trackLQ } = useLQTracking()
  const { generateAIAttributes } = useGPTQualificationAttributes()

  useEffect(() => {
    return () => {
      unregister('prompt')
    }
  }, [unregister])

  const onAIAttributesSuccess = (data: Attribute[]) => {
    if (!data.length) {
      return
    }
    const formValues = getValues()
    const formAIAttributesValue = transformAttributes(data)

    resetField('attributes', {
      defaultValue: formAIAttributesValue,
    })
    setValue('foundAIAttributes', formAIAttributesValue)
    resetField('prompt', {
      defaultValue: getValues('prompt'),
    })

    trackLQ(AILQTrackAction.GENERATE_ATTRIBUTES, {
      criteria: data,
      prompt: formValues.prompt,
    })
  }

  return (
    <>
      {isLoading ? (
        <Skeleton isLoading height={165} />
      ) : (
        <Textarea
          label="How do you define a qualified lead?"
          placeholder={PROMPT_PLACEHOLDER}
          description={PROMPT_DESCRIPTION}
          size="full-width"
          {...register('prompt')}
          disabled={disabled}
        />
      )}
      <Button
        css={{ marginTop: '$1' }}
        variant="education"
        onClick={() => {
          onGeneratingAttributes({ isLoading: true })

          if (!isLoading)
            generateAIAttributes.mutate(getValues('prompt'), {
              onSuccess: (data) => onAIAttributesSuccess(data),
              onSettled: () => onGeneratingAttributes({ isLoading: false }),
            })
        }}
        disabled={isDiscoverAttributesDisabled}
      >
        <Button.Icon icon={faSparkles} />
        Discover Attributes
      </Button>
    </>
  )
}

export default AIPrompt
